import { useState } from 'react'
import { useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { selfPayInsuranceBody, selfPayOffered } from '../../utils/selfPayInsurance'
import SelfPayConfirmedNote from '../SelfPayConfirmedNote'
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

/**
 * ContinueWithoutInsurance — PROD-1132 Q1 (Alex 2026-10-18): the before-share block still stops a
 * share while insurance is not on file ("I'll add my card later", or never answered), and offers
 * self-pay right there.
 *
 * Offered only by selfPayOffered (utils/selfPayInsurance.js): insurance is the ONLY missing section
 * and no carrier + member ID is on file (review HIGH-1, MEDIUM-1). The tap saves nothing: like the
 * onboarding step it shows "Self-pay confirmed" + its hint with Continue and "I have insurance after
 * all" (review MEDIUM-2). Continue saves the self-pay answer; only a saved answer continues the share
 * (`onDone`), a failed save says so and changes nothing. Used by ShareHubScreen, DiagnoseScreen and
 * ShareScreen.
 */
export default function ContinueWithoutInsurance({ missing, onDone, className = '' }) {
  const { t } = useI18n()
  const syncSection = useProfileStore((s) => s.syncSection)
  const insurance = useProfileStore((s) => s.profile?.insurance)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  if (!selfPayOffered(missing, insurance)) return null

  function choose() {
    setFailed(false)
    setConfirming(true)
  }

  function change() {
    if (busy) return
    setFailed(false)
    setConfirming(false)
  }

  async function save() {
    if (busy) return
    setBusy(true)
    setFailed(false)
    let result = null
    try {
      result = await syncSection('insurance', selfPayInsuranceBody())
    } catch (e) {
      result = null
    }
    setBusy(false)
    if (result && result.success) {
      if (typeof onDone === 'function') onDone()
    } else {
      setFailed(true)
    }
  }

  if (!confirming) {
    return (
      <div className={className} data-testid="continue-without-insurance">
        <button
          type="button"
          onClick={choose}
          className="w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98]"
        >
          {t('onboarding.insurance.selfPay')}
        </button>
      </div>
    )
  }

  return (
    <div className={`${className} text-left`} data-testid="continue-without-insurance">
      <SelfPayConfirmedNote />
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="w-full py-3 rounded-full bg-[#15354F] text-white text-[14px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] disabled:opacity-60"
      >
        {busy && <OrellaSpinner size={16} tone="inherit" />}
        {t('common.continue')}
      </button>
      <button
        type="button"
        onClick={change}
        disabled={busy}
        className="w-full py-2.5 mt-1.5 rounded-full border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[14px] font-semibold active:scale-[0.98] disabled:opacity-60"
      >
        {t('onboarding.insurance.changedMind')}
      </button>
      {failed && (
        <p className="text-[12px] text-red-600 text-center mt-1.5" role="alert">{t('insuranceSearch.saveFailed')}</p>
      )}
    </div>
  )
}
